import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faArrowRight,
	faCheck,
	faChevronDown,
	faUserPlus,
	faCalendarPlus,
	faRocket,
	faPercent,
	faBolt,
	faQrcode,
	faChartLine,
	faShieldHalved,
	faMobileScreenButton,
	faXmark,
	faMusic,
	faMicrophone,
	faGraduationCap,
	faHandshake,
	faStar,
	faQuoteLeft,
	faHeadset,
	faLock,
	faGlobe,
} from "@fortawesome/free-solid-svg-icons";
import { usePageTitle } from "../hooks/usePageTitle";
import HeroNatural from "../components/HeroNatural";

/* ─── data ─── */

const features = [
	{
		icon: faPercent,
		title: "手数料0%",
		desc: "一般的なサービスの5〜10%とは違い、販売手数料は一切かかりません。売上はすべてあなたのものです。",
		highlight: "売上100%が手元に",
	},
	{
		icon: faBolt,
		title: "かんたんイベント作成",
		desc: "直感的な操作で、最短5分でイベントページを作成・公開。面倒な手続きは不要です。",
		highlight: "最短5分で公開",
	},
	{
		icon: faQrcode,
		title: "QRコード入場管理",
		desc: "スマホでQRコードを読み取るだけ。紙チケット不要で、当日の受付がスムーズに。",
		highlight: "ペーパーレス受付",
	},
	{
		icon: faChartLine,
		title: "リアルタイム売上管理",
		desc: "販売状況をダッシュボードからいつでも確認。売上データのCSVエクスポートにも対応。",
		highlight: "データドリブン運営",
	},
	{
		icon: faShieldHalved,
		title: "安心のStripe決済",
		desc: "世界で最も使われている決済基盤Stripeと連携。Visa、Mastercard、JCB等に対応。",
		highlight: "PCI DSS準拠",
	},
	{
		icon: faMobileScreenButton,
		title: "スマホ完全対応",
		desc: "イベント管理も、チケット購入も、入場確認も、すべてスマートフォンから操作できます。",
		highlight: "どこでも管理",
	},
];

const useCases = [
	{
		icon: faMusic,
		title: "音楽ライブ・フェス",
		desc: "ワンマンライブから大規模フェスまで。複数チケット種別、早割設定も自由自在。",
	},
	{
		icon: faMicrophone,
		title: "セミナー・講演会",
		desc: "定員管理と参加者リストの自動生成。当日の受付もQRで効率化。",
	},
	{
		icon: faGraduationCap,
		title: "ワークショップ・教室",
		desc: "少人数の教室から大人数の体験イベントまで。リピーター管理にも。",
	},
	{
		icon: faHandshake,
		title: "交流会・パーティー",
		desc: "参加費の事前徴収で当日キャンセル率を低減。スムーズな受付を実現。",
	},
];

const comparisons = [
	{ label: "販売手数料", ours: "0%", a: "4.9% + ¥99", b: "〜8%" },
	{ label: "決済手数料", ours: "3.6%", a: "別途あり", b: "含む" },
	{ label: "初期費用", ours: "¥0", a: "¥0", b: "¥0" },
	{ label: "月額費用", ours: "¥0", a: "¥0〜", b: "¥0〜" },
	{ label: "QR入場管理", ours: true, a: true, b: false },
	{ label: "即日利用", ours: true, a: false, b: true },
	{ label: "日本語サポート", ours: true, a: true, b: false },
];

const testimonials = [
	{
		name: "田中 優子",
		role: "音楽イベント主催者",
		text: "手数料0%は本当に大きいです。以前のサービスでは売上の8%が手数料で消えていました。QR入場管理も便利で、当日のオペレーションが格段にラクになりました。",
		rating: 5,
	},
	{
		name: "佐藤 健一",
		role: "セミナー運営",
		text: "登録から最初のイベント公開まで10分かからなかった。直感的に操作できるのが素晴らしい。売上データもリアルタイムで確認できるので、次回イベントの計画にも役立っています。",
		rating: 5,
	},
	{
		name: "山本 美咲",
		role: "ワークショップ講師",
		text: "少人数のワークショップでも気軽に使えます。事前決済にしたことでドタキャン率が劇的に下がりました。参加者リストの管理もラクで助かっています。",
		rating: 5,
	},
];

const faqs = [
	{
		q: "本当に販売手数料は無料ですか？",
		a: "はい、完全に無料です。チケットの販売手数料は一切いただきません。売上はすべて主催者様のお手元に届きます。イベント規模や開催回数に関わらず、ずっと0%です。",
	},
	{
		q: "決済手数料はかかりますか？",
		a: "クレジットカード決済の処理手数料（3.6%）のみ、決済代行会社Stripeに直接お支払いいただきます。当プラットフォームへの手数料は0%です。無料チケットの場合、手数料は一切かかりません。",
	},
	{
		q: "どのような決済方法に対応していますか？",
		a: "Visa、Mastercard、American Express、JCBなど主要なクレジットカードに対応しています。Stripeの安全な決済基盤を使用しているため、カード情報は当社サーバーを経由しません。",
	},
	{
		q: "イベント作成にどれくらい時間がかかりますか？",
		a: "基本的な情報の入力だけなら最短5分で公開できます。画像やチケット種別の設定を含めても、15〜20分程度です。テンプレート機能を使えば、次回以降はさらに短時間で作成可能です。",
	},
	{
		q: "売上はいつ振り込まれますか？",
		a: "イベント終了後、Stripeを通じて指定の銀行口座へ自動で振り込まれます。通常、数営業日以内です。Stripeダッシュボードから振込状況をいつでも確認できます。",
	},
	{
		q: "個人でも登録できますか？",
		a: "はい。特別な審査はなく、個人の方でも法人の方でも、どなたでもご登録いただけます。メールアドレスと基本情報のみで、すぐにご利用を開始できます。",
	},
	{
		q: "有料プランはありますか？",
		a: "現在、すべての機能を無料でご利用いただけます。将来的にプレミアム機能を追加する予定ですが、基本機能と0%手数料は今後も変わりません。",
	},
	{
		q: "最大何枚までチケットを販売できますか？",
		a: "販売枚数に上限はありません。小規模な10名のワークショップから、数千名規模のフェスティバルまで対応しています。",
	},
];

/* ─── hooks ─── */

function useScrollReveal() {
	const observerRef = useRef<IntersectionObserver | null>(null);

	const init = useCallback(() => {
		if (observerRef.current) return;
		observerRef.current = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						entry.target.classList.add("revealed");
						observerRef.current?.unobserve(entry.target);
					}
				});
			},
			{ threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
		);

		document
			.querySelectorAll(".reveal, .reveal-scale")
			.forEach((el) => observerRef.current?.observe(el));
	}, []);

	useEffect(() => {
		init();
		return () => observerRef.current?.disconnect();
	}, [init]);
}

/* ─── component ─── */

const OrganizerLandingPage: React.FC = () => {
	usePageTitle("主催者向け - チケット販売プラットフォーム");
	useScrollReveal();
	const [openFaq, setOpenFaq] = useState<number | null>(null);

	return (
		<div className="overflow-x-hidden">
			{/* ==================== Hero ==================== */}
			<section className="relative isolate min-h-[80vh] sm:min-h-[75vh] flex items-center overflow-hidden">
				<HeroNatural />

				<div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 md:py-40">
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
						{/* Left — copy */}
						<div>
							<p className="text-xs sm:text-sm font-medium tracking-[0.2em] uppercase text-violet-600/60 dark:text-violet-400/50 mb-6">
								For Organizers
							</p>

							<h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-[4.25rem] font-bold leading-[1.08] tracking-tight text-gray-900 dark:text-white mb-6">
								チケット販売を、
								<br />
								<span className="text-violet-600 dark:text-violet-400">
									もっとシンプルに。
								</span>
							</h1>

							<p className="text-lg sm:text-xl text-gray-500 dark:text-gray-400 max-w-md leading-relaxed mb-4">
								販売手数料は完全無料。イベント作成から入場管理まで、必要な機能をひとつに。
							</p>

							<ul className="space-y-2 mb-10">
								{[
									"販売手数料ずっと0%",
									"最短5分でイベント公開",
									"QRコードで即入場管理",
								].map((item, i) => (
									<li
										key={i}
										className="flex items-center gap-2.5 text-sm sm:text-base text-gray-600 dark:text-gray-300"
									>
										<span className="flex-shrink-0 w-5 h-5 rounded-full bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center">
											<FontAwesomeIcon
												icon={faCheck}
												className="h-2.5 w-2.5 text-violet-600 dark:text-violet-400"
											/>
										</span>
										{item}
									</li>
								))}
							</ul>

							<div className="flex flex-col sm:flex-row gap-3">
								<Link
									to="/organizer/register"
									className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white px-8 py-4 rounded-xl text-base font-medium transition-all shadow-lg hover:shadow-xl group"
								>
									<span>無料で主催者登録する</span>
									<FontAwesomeIcon
										icon={faArrowRight}
										className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
									/>
								</Link>
								<a
									href="#features"
									className="inline-flex items-center justify-center gap-2 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 px-6 py-4 rounded-xl text-base font-medium hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all"
								>
									詳しく見る
								</a>
							</div>
						</div>

						{/* Right — dashboard mockup */}
						<div className="hidden lg:block relative">
							<div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl shadow-violet-200/30 dark:shadow-violet-900/20 border border-gray-200 dark:border-gray-800 overflow-hidden">
								{/* Title bar */}
								<div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-900/80">
									<div className="flex gap-1.5">
										<div className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
										<div className="w-2.5 h-2.5 rounded-full bg-yellow-400/80" />
										<div className="w-2.5 h-2.5 rounded-full bg-green-400/80" />
									</div>
									<div className="flex-1 text-center text-[11px] text-gray-400 dark:text-gray-600 font-medium">
										ダッシュボード
									</div>
								</div>

								{/* Mock dashboard */}
								<div className="p-5 space-y-4">
									{/* Stats row */}
									<div className="grid grid-cols-3 gap-3">
										{[
											{
												label: "今月の売上",
												value: "¥482,600",
												change: "+12.3%",
											},
											{
												label: "販売枚数",
												value: "234枚",
												change: "+8.1%",
											},
											{
												label: "手数料",
												value: "¥0",
												change: "ずっと0%",
											},
										].map((s, i) => (
											<div
												key={i}
												className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-3"
											>
												<div className="text-[10px] text-gray-400 dark:text-gray-500 mb-1">
													{s.label}
												</div>
												<div className="text-sm font-bold text-gray-900 dark:text-white">
													{s.value}
												</div>
												<div className="text-[10px] text-emerald-500 font-medium mt-0.5">
													{s.change}
												</div>
											</div>
										))}
									</div>

									{/* Mini chart */}
									<div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">
										<div className="flex items-center justify-between mb-3">
											<div className="text-xs font-medium text-gray-600 dark:text-gray-400">
												売上推移
											</div>
											<div className="text-[10px] text-gray-400 dark:text-gray-600">
												過去30日間
											</div>
										</div>
										<div className="flex items-end gap-1 h-16">
											{[
												35, 42, 28, 55, 48, 62, 45, 70,
												58, 75, 68, 82, 72, 90, 85, 95,
											].map((h, i) => (
												<div
													key={i}
													className="flex-1 rounded-sm bg-gradient-to-t from-violet-500 to-purple-400 dark:from-violet-600 dark:to-purple-500 opacity-80"
													style={{
														height: `${h}%`,
													}}
												/>
											))}
										</div>
									</div>

									{/* Recent orders */}
									<div className="space-y-2">
										<div className="text-xs font-medium text-gray-600 dark:text-gray-400">
											最近の注文
										</div>
										{[
											{
												name: "Summer Jazz Night",
												qty: "2枚",
												amount: "¥6,600",
											},
											{
												name: "アコースティックライブ",
												qty: "1枚",
												amount: "¥3,800",
											},
											{
												name: "DJ Party Vol.12",
												qty: "4枚",
												amount: "¥12,000",
											},
										].map((o, i) => (
											<div
												key={i}
												className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-800 last:border-0"
											>
												<div>
													<div className="text-xs font-medium text-gray-700 dark:text-gray-300">
														{o.name}
													</div>
													<div className="text-[10px] text-gray-400 dark:text-gray-500">
														{o.qty}
													</div>
												</div>
												<div className="text-xs font-bold text-gray-900 dark:text-white">
													{o.amount}
												</div>
											</div>
										))}
									</div>
								</div>
							</div>

							{/* Decorative glow */}
							<div className="absolute -inset-4 bg-gradient-to-br from-violet-400/20 to-purple-600/20 dark:from-violet-600/10 dark:to-purple-800/10 rounded-3xl blur-2xl -z-10" />
						</div>
					</div>
				</div>
			</section>

			{/* ==================== Stats Bar ==================== */}
			<section className="border-t border-gray-100 dark:border-gray-800/50">
				<div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
					<div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-0 lg:divide-x divide-gray-200 dark:divide-gray-700">
						{[
							{ value: "0%", label: "販売手数料" },
							{ value: "5分", label: "でイベント公開" },
							{ value: "QR", label: "入場管理" },
							{ value: "24h", label: "いつでも管理" },
						].map((stat, i) => (
							<div key={i} className="text-center lg:px-8">
								<div className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-1">
									{stat.value}
								</div>
								<div className="text-sm text-gray-400 dark:text-gray-500">
									{stat.label}
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* ==================== Features ==================== */}
			<section
				id="features"
				className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8"
			>
				<div className="max-w-6xl mx-auto">
					<div className="mb-12 sm:mb-14 reveal">
						<p className="text-xs sm:text-sm font-medium tracking-widest uppercase text-violet-600/50 dark:text-violet-400/40 mb-2">
							Features
						</p>
						<h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
							選ばれる理由
						</h2>
						<p className="text-base sm:text-lg text-gray-500 dark:text-gray-400 max-w-lg">
							イベント主催に必要なすべてが、ここに揃っています。
						</p>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
						{features.map((f, i) => (
							<div
								key={i}
								className={`reveal stagger-${
									i + 1
								} bg-white dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-800 hover:border-violet-200 dark:hover:border-violet-900/50 transition-colors p-7 sm:p-8 group`}
							>
								<div className="flex items-start justify-between mb-5">
									<div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 text-white">
										<FontAwesomeIcon
											icon={f.icon}
											className="h-5 w-5"
										/>
									</div>
									<span className="text-[11px] font-medium text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-950/30 px-2.5 py-1 rounded-full">
										{f.highlight}
									</span>
								</div>
								<h3 className="font-bold text-lg text-gray-900 dark:text-white mb-2">
									{f.title}
								</h3>
								<p className="text-base text-gray-500 dark:text-gray-400 leading-relaxed">
									{f.desc}
								</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* ==================== Fee Callout ==================== */}
			<section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 border-t border-gray-100 dark:border-gray-800/50">
				<div className="max-w-5xl mx-auto text-center reveal">
					<div className="text-8xl sm:text-9xl lg:text-[10rem] font-bold text-gray-900 dark:text-white leading-none mb-4">
						0
						<span className="text-violet-600 dark:text-violet-400">
							%
						</span>
					</div>
					<h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4">
						販売手数料は、ずっと無料
					</h2>
					<p className="text-base sm:text-lg text-gray-500 dark:text-gray-400 max-w-lg mx-auto leading-relaxed mb-10">
						売上はすべてあなたのもの。決済手数料（3.6%）のみStripeへ直接お支払いいただく形です。当プラットフォームの利用料は一切かかりません。
					</p>

					{/* Fee breakdown */}
					<div className="max-w-md mx-auto bg-white dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-800 p-6 text-left">
						<div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-4">
							例）¥5,000のチケットが売れた場合
						</div>
						<div className="space-y-3">
							<div className="flex items-center justify-between">
								<span className="text-sm text-gray-600 dark:text-gray-400">
									チケット販売価格
								</span>
								<span className="text-sm font-bold text-gray-900 dark:text-white">
									¥5,000
								</span>
							</div>
							<div className="flex items-center justify-between">
								<span className="text-sm text-gray-600 dark:text-gray-400">
									プラットフォーム手数料
								</span>
								<span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
									¥0
								</span>
							</div>
							<div className="flex items-center justify-between">
								<span className="text-sm text-gray-600 dark:text-gray-400">
									Stripe決済手数料（3.6%）
								</span>
								<span className="text-sm font-medium text-gray-500 dark:text-gray-400">
									-¥180
								</span>
							</div>
							<div className="border-t border-gray-100 dark:border-gray-800 pt-3 flex items-center justify-between">
								<span className="text-sm font-bold text-gray-900 dark:text-white">
									あなたの受取額
								</span>
								<span className="text-lg font-bold text-violet-600 dark:text-violet-400">
									¥4,820
								</span>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* ==================== Comparison ==================== */}
			<section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 bg-gray-50/80 dark:bg-gray-900/30">
				<div className="max-w-4xl mx-auto">
					<div className="text-center mb-12 sm:mb-14 reveal">
						<p className="text-xs sm:text-sm font-medium tracking-widest uppercase text-violet-600/50 dark:text-violet-400/40 mb-2">
							Comparison
						</p>
						<h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
							他社サービスとの比較
						</h2>
						<p className="text-base sm:text-lg text-gray-500 dark:text-gray-400 max-w-lg mx-auto">
							同じチケットを販売するなら、手数料の差が利益の差になります。
						</p>
					</div>

					<div className="reveal bg-white dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden">
						{/* Header */}
						<div className="grid grid-cols-4 text-center border-b border-gray-100 dark:border-gray-800">
							<div className="p-4 text-sm font-medium text-gray-400 dark:text-gray-500" />
							<div className="p-4 bg-violet-50/80 dark:bg-violet-950/20 border-x border-violet-100 dark:border-violet-900/30">
								<div className="text-sm font-bold text-violet-600 dark:text-violet-400">
									b+ ticket
								</div>
							</div>
							<div className="p-4">
								<div className="text-sm font-medium text-gray-400 dark:text-gray-500">
									サービスA
								</div>
							</div>
							<div className="p-4">
								<div className="text-sm font-medium text-gray-400 dark:text-gray-500">
									サービスB
								</div>
							</div>
						</div>

						{/* Rows */}
						{comparisons.map((row, i) => (
							<div
								key={i}
								className="grid grid-cols-4 text-center border-b border-gray-50 dark:border-gray-800/50 last:border-0"
							>
								<div className="p-4 text-sm text-gray-600 dark:text-gray-400 text-left font-medium">
									{row.label}
								</div>
								<div className="p-4 bg-violet-50/40 dark:bg-violet-950/10 border-x border-violet-100/50 dark:border-violet-900/20">
									{typeof row.ours === "boolean" ? (
										<FontAwesomeIcon
											icon={
												row.ours ? faCheck : faXmark
											}
											className={`h-4 w-4 ${
												row.ours
													? "text-violet-600 dark:text-violet-400"
													: "text-gray-300 dark:text-gray-600"
											}`}
										/>
									) : (
										<span className="text-sm font-bold text-violet-600 dark:text-violet-400">
											{row.ours}
										</span>
									)}
								</div>
								<div className="p-4">
									{typeof row.a === "boolean" ? (
										<FontAwesomeIcon
											icon={row.a ? faCheck : faXmark}
											className={`h-4 w-4 ${
												row.a
													? "text-gray-400 dark:text-gray-500"
													: "text-gray-300 dark:text-gray-600"
											}`}
										/>
									) : (
										<span className="text-sm text-gray-500 dark:text-gray-400">
											{row.a}
										</span>
									)}
								</div>
								<div className="p-4">
									{typeof row.b === "boolean" ? (
										<FontAwesomeIcon
											icon={row.b ? faCheck : faXmark}
											className={`h-4 w-4 ${
												row.b
													? "text-gray-400 dark:text-gray-500"
													: "text-gray-300 dark:text-gray-600"
											}`}
										/>
									) : (
										<span className="text-sm text-gray-500 dark:text-gray-400">
											{row.b}
										</span>
									)}
								</div>
							</div>
						))}
					</div>

					<p className="text-center text-xs text-gray-400 dark:text-gray-600 mt-4">
						※ 2024年12月時点の各社公開情報に基づく比較です
					</p>
				</div>
			</section>

			{/* ==================== Use Cases ==================== */}
			<section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
				<div className="max-w-6xl mx-auto">
					<div className="text-center mb-12 sm:mb-14 reveal">
						<p className="text-xs sm:text-sm font-medium tracking-widest uppercase text-violet-600/50 dark:text-violet-400/40 mb-2">
							Use Cases
						</p>
						<h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
							あらゆるイベントに対応
						</h2>
						<p className="text-base sm:text-lg text-gray-500 dark:text-gray-400 max-w-lg mx-auto">
							規模やジャンルを問わず、あなたのイベントをサポートします。
						</p>
					</div>

					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
						{useCases.map((uc, i) => (
							<div
								key={i}
								className={`reveal stagger-${
									i + 1
								} relative bg-white dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-800 hover:border-violet-200 dark:hover:border-violet-900/50 transition-colors p-6 sm:p-7 text-center group`}
							>
								<div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-violet-50 dark:bg-violet-950/30 text-violet-600 dark:text-violet-400 mb-5 group-hover:bg-gradient-to-r group-hover:from-violet-500 group-hover:to-purple-600 group-hover:text-white transition-all">
									<FontAwesomeIcon
										icon={uc.icon}
										className="h-6 w-6"
									/>
								</div>
								<h3 className="font-bold text-base sm:text-lg text-gray-900 dark:text-white mb-2">
									{uc.title}
								</h3>
								<p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
									{uc.desc}
								</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* ==================== How it works ==================== */}
			<section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-gray-50/80 dark:bg-gray-900/30">
				<div className="max-w-5xl mx-auto">
					<div className="text-center mb-16 sm:mb-20 reveal">
						<p className="text-xs sm:text-sm font-medium tracking-widest uppercase text-violet-600 dark:text-violet-400 mb-3">
							How it works
						</p>
						<h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
							始め方はかんたん3ステップ
						</h2>
						<p className="text-base sm:text-lg text-gray-500 dark:text-gray-400 max-w-lg mx-auto">
							アカウント登録からチケット販売開始まで、最短10分で完了します。
						</p>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-12">
						{[
							{
								num: "01",
								title: "無料アカウント登録",
								desc: "メールアドレスで簡単登録。基本情報を入力するだけで、わずか2分で完了します",
								icon: faUserPlus,
							},
							{
								num: "02",
								title: "イベントを作成",
								desc: "イベント情報を入力して、チケットの種類と価格を設定するだけ。最短5分で公開",
								icon: faCalendarPlus,
							},
							{
								num: "03",
								title: "チケット販売開始",
								desc: "公開ボタンを押せば、すぐにチケット販売がスタート。売上はダッシュボードで確認",
								icon: faRocket,
							},
						].map((step, i) => (
							<div
								key={i}
								className={`reveal stagger-${
									i + 1
								} relative text-center group`}
							>
								<div className="text-7xl sm:text-8xl font-bold text-violet-100 dark:text-violet-900/30 leading-none mb-4 select-none">
									{step.num}
								</div>
								<div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 text-white mb-5 -mt-10 relative">
									<FontAwesomeIcon
										icon={step.icon}
										className="h-6 w-6"
									/>
								</div>
								<h3 className="font-bold text-lg sm:text-xl text-gray-900 dark:text-white mb-3">
									{step.title}
								</h3>
								<p className="text-base text-gray-500 dark:text-gray-400 leading-relaxed max-w-xs mx-auto">
									{step.desc}
								</p>
							</div>
						))}
					</div>

					<div className="text-center mt-16 sm:mt-20 reveal">
						<Link
							to="/organizer/register"
							className="inline-flex items-center gap-2.5 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white px-8 py-4 rounded-xl text-base font-medium transition-all shadow-lg hover:shadow-xl group"
						>
							<span>無料で主催者登録する</span>
							<FontAwesomeIcon
								icon={faArrowRight}
								className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
							/>
						</Link>
					</div>
				</div>
			</section>

			{/* ==================== Testimonials ==================== */}
			<section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
				<div className="max-w-6xl mx-auto">
					<div className="text-center mb-12 sm:mb-14 reveal">
						<p className="text-xs sm:text-sm font-medium tracking-widest uppercase text-violet-600/50 dark:text-violet-400/40 mb-2">
							Testimonials
						</p>
						<h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
							ご利用者の声
						</h2>
						<p className="text-base sm:text-lg text-gray-500 dark:text-gray-400 max-w-lg mx-auto">
							実際にご利用いただいている主催者様からのフィードバック。
						</p>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-3 gap-5">
						{testimonials.map((t, i) => (
							<div
								key={i}
								className={`reveal stagger-${
									i + 1
								} bg-white dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-800 p-7 sm:p-8`}
							>
								<FontAwesomeIcon
									icon={faQuoteLeft}
									className="h-6 w-6 text-violet-200 dark:text-violet-900/50 mb-4"
								/>
								<p className="text-base text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
									{t.text}
								</p>
								<div className="flex items-center gap-1 mb-3">
									{Array.from({ length: t.rating }).map(
										(_, j) => (
											<FontAwesomeIcon
												key={j}
												icon={faStar}
												className="h-3.5 w-3.5 text-amber-400"
											/>
										)
									)}
								</div>
								<div>
									<div className="text-sm font-bold text-gray-900 dark:text-white">
										{t.name}
									</div>
									<div className="text-xs text-gray-400 dark:text-gray-500">
										{t.role}
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* ==================== Trust & Security ==================== */}
			<section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 border-t border-gray-100 dark:border-gray-800/50">
				<div className="max-w-5xl mx-auto">
					<div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
						{[
							{
								icon: faLock,
								title: "SSL暗号化通信",
								desc: "すべての通信を暗号化",
							},
							{
								icon: faShieldHalved,
								title: "PCI DSS準拠",
								desc: "カード情報の安全管理",
							},
							{
								icon: faHeadset,
								title: "メールサポート",
								desc: "お問い合わせに迅速対応",
							},
							{
								icon: faGlobe,
								title: "安定稼働",
								desc: "高い可用性と信頼性",
							},
						].map((item, i) => (
							<div
								key={i}
								className="text-center reveal"
							>
								<div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 mb-3">
									<FontAwesomeIcon
										icon={item.icon}
										className="h-5 w-5"
									/>
								</div>
								<h3 className="text-sm font-bold text-gray-900 dark:text-white mb-1">
									{item.title}
								</h3>
								<p className="text-xs text-gray-400 dark:text-gray-500">
									{item.desc}
								</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* ==================== FAQ ==================== */}
			<section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 bg-gray-50/80 dark:bg-gray-900/30">
				<div className="max-w-3xl mx-auto">
					<div className="mb-12 reveal">
						<p className="text-xs sm:text-sm font-medium tracking-widest uppercase text-violet-600/50 dark:text-violet-400/40 mb-2">
							FAQ
						</p>
						<h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
							よくある質問
						</h2>
					</div>

					<div className="divide-y divide-gray-200 dark:divide-gray-800 border-y border-gray-200 dark:border-gray-800 reveal">
						{faqs.map((faq, i) => (
							<div key={i}>
								<button
									onClick={() =>
										setOpenFaq(
											openFaq === i ? null : i
										)
									}
									className="w-full flex items-center justify-between py-6 text-left group"
								>
									<span className="text-base sm:text-lg font-medium text-gray-900 dark:text-white pr-4 group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors">
										{faq.q}
									</span>
									<FontAwesomeIcon
										icon={faChevronDown}
										className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${
											openFaq === i
												? "rotate-180"
												: ""
										}`}
									/>
								</button>
								<div
									style={{
										maxHeight:
											openFaq === i ? "300px" : "0",
										overflow: "hidden",
										transition:
											"max-height 0.2s ease",
									}}
								>
									<p className="text-base text-gray-500 dark:text-gray-400 leading-relaxed pb-6">
										{faq.a}
									</p>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* ==================== CTA ==================== */}
			<section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-violet-600 via-purple-700 to-indigo-800 dark:from-violet-900 dark:via-purple-950 dark:to-indigo-950 relative overflow-hidden">
				<div className="absolute top-[-20%] right-[-10%] w-[50%] h-[80%] rounded-full blur-[100px] bg-white/[0.07] pointer-events-none" />
				<div className="absolute bottom-[-30%] left-[-5%] w-[40%] h-[60%] rounded-full blur-[80px] bg-purple-400/10 pointer-events-none" />

				<div className="max-w-3xl mx-auto relative text-center">
					<p className="text-sm font-medium tracking-widest uppercase text-violet-200/70 dark:text-violet-300/50 mb-4">
						Get Started
					</p>
					<h2 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-5">
						さあ、イベントを
						<br />
						始めよう。
					</h2>
					<p className="text-base sm:text-lg text-violet-100/60 dark:text-violet-200/40 mb-10 max-w-md mx-auto">
						登録は無料、わずか2分で完了します。販売手数料は0%、あなたの売上はすべてあなたのものです。
					</p>

					<div className="flex flex-col sm:flex-row items-center justify-center gap-3">
						<Link
							to="/organizer/register"
							className="inline-flex items-center justify-center gap-2 bg-white text-violet-700 px-8 py-4 rounded-xl text-base font-medium hover:bg-violet-50 transition-colors shadow-lg"
						>
							<span>無料で主催者登録する</span>
							<FontAwesomeIcon
								icon={faArrowRight}
								className="h-4 w-4"
							/>
						</Link>
					</div>

					{/* Trust indicators */}
					<div className="flex flex-wrap items-center justify-center gap-6 mt-12 text-violet-200/40">
						{[
							"販売手数料0%",
							"Stripe決済",
							"SSL暗号化通信",
							"いつでも解約OK",
						].map((text, i) => (
							<div
								key={i}
								className="flex items-center gap-1.5"
							>
								<FontAwesomeIcon
									icon={faCheck}
									className="h-3 w-3"
								/>
								<span className="text-sm">{text}</span>
							</div>
						))}
					</div>
				</div>
			</section>
		</div>
	);
};

export default OrganizerLandingPage;
